/**
 * How wide the inline right panel may be.
 *
 * The ceiling used to be `720`, a constant. On any window wider than about
 * 1400px that number, not the layout, was what stopped the panel — the
 * transcript column had a floor it could not be dragged below (~894px on a
 * 1914px monitor) and widening the panel simply stopped working partway
 * through the drag.
 *
 * The bound that actually matters belongs to the *sibling*: the transcript has
 * a minimum, and the panel may have whatever is left. Stated that way the
 * ceiling is a function of the row, so it re-derives itself when the window
 * resizes or the left sidebar is dragged, and there is no number to pick wrong.
 *
 * The viewport fraction is a second, softer bound for wide monitors, where
 * "the row minus 360px" would otherwise let the panel swallow the window.
 *
 * All px, no rem: Forge's root font-size is user-adjustable (11px-20px) and the
 * drag clamp only ever sees pixels. These were once `18 * 16` and `42 * 16`
 * beside a `rem` CSS clamp, which floored the panel at 252px while the drag
 * clamp believed 288px — two numbers for one bound, disagreeing by 14/16.
 */
export const RIGHT_SIDEBAR_MIN_WIDTH = 360;
export const RIGHT_SIDEBAR_DEFAULT_WIDTH = 540;
/** Only binds on wide monitors; the row bound is what protects narrow ones. */
export const RIGHT_PANEL_MAX_WIDTH_FRACTION = 0.7;
/** What the transcript column keeps for itself when the panel is widest. */
export const SIBLING_COLUMN_MIN_WIDTH = 360;

/**
 * The panel's ceiling for a given window and content row.
 *
 * `rowWidth` is undefined until the row has been measured — on the first
 * render, and in any host that has no `ResizeObserver`. The viewport fraction
 * alone answers those cases; it is looser than the row bound but never unsafe,
 * because the row bound only ever tightens it.
 *
 * The `Math.max` floor is load-bearing rather than defensive. When the row
 * cannot fit both columns' minimums the transcript is the one that yields, and
 * a max below the min would otherwise invert the clamp — `clampSidebarWidth`
 * resolves an inverted range to the maximum, so the panel would snap to a
 * sliver and drag-end persistence would write that sliver to storage.
 */
export function resolveRightPanelMaxWidth(viewportWidth: number, rowWidth?: number): number {
  const fractionCap = Math.floor(viewportWidth * RIGHT_PANEL_MAX_WIDTH_FRACTION);
  const rowCap =
    rowWidth === undefined
      ? Number.POSITIVE_INFINITY
      : Math.floor(rowWidth) - SIBLING_COLUMN_MIN_WIDTH;
  return Math.max(RIGHT_SIDEBAR_MIN_WIDTH, Math.min(fractionCap, rowCap));
}

/**
 * The width to open at, given what was stored and how much room there is now.
 *
 * The stored value is a preference clamped on read, not a fact applied
 * verbatim: a width dragged out on an external monitor has to survive being
 * reopened on a laptop. Storage keeps the larger number so the panel returns
 * to it once the room comes back.
 */
export function resolveInitialRightPanelWidth(
  storedWidth: number | null,
  viewportWidth: number,
  rowWidth?: number,
  /** Surfaces that open wider than a chat panel — the pull-request list — pass
   *  their own opening width. It is still clamped like any other. */
  defaultWidth: number = RIGHT_SIDEBAR_DEFAULT_WIDTH,
): number {
  const preferredWidth =
    storedWidth === null ? defaultWidth : Math.max(RIGHT_SIDEBAR_MIN_WIDTH, storedWidth);
  return Math.min(
    Math.max(RIGHT_SIDEBAR_MIN_WIDTH, preferredWidth),
    resolveRightPanelMaxWidth(viewportWidth, rowWidth),
  );
}
